import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { distinctUntilChanged, lastValueFrom } from 'rxjs';
import { DisabilityProof } from '../../../../api/app-types/disability-proof.app';
import { CarouselElement, NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { MatDialog } from '@angular/material/dialog';
import { DisabilityProofDeleteDialog } from '../disability-proof-delete-dialog/disability-proof-delete-dialog';
import { Metadata } from '../../../../api/api-types/api-response.api';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import {
	DOCUMENT_CATEGORY,
	DocumentEntry,
	DocumentFile,
	DocumentNavItem,
} from '../../../../api/app-types/document.app';
import { LvrDocumentItem } from '../../../../core/components/document-item/document-item';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { DisabilityStatusInitial } from '../../../../api/app-types/disability-status.app';

/**
 * Component to display the details of a disability proof.
 */
@Component({
	selector: 'lvr-disability-proof-detail',
	templateUrl: './disability-proof-detail.html',
	styleUrl: './disability-proof-detail.scss',
	imports: [TranslocoPipe, DatePipe, NavCarousel, LvrHeading, LvrDocumentItem],
})
export class DisabilityProofDetail {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly data = toSignal(this.route.data.pipe(distinctUntilChanged()));
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);

	private readonly clientId = computed(() => this.data()!['clientId'] as number);
	private readonly metadata = computed(() => this.data()!['proof'].metadata as Metadata);
	public readonly proof = computed(() => this.data()!['proof'].data as DisabilityProof);
	public readonly proofNeighbors = computed(() => this.data()!['proofNeighbors']);
	public readonly shouldNotBeDeleted = computed(() => this.metadata().editOnlyFields!.length > 0);
	public readonly backLink = computed(() => `/desk/client/${this.clientId()}/disabilities`);
	public readonly shouldShowOtherText = computed(() => this.proof().status.content.toLowerCase() === 'sonstiges:');
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly canEditDisabilityProof = hasPermission(this.session.permissions, 'Documents.DisabilityReport.Edit');
	public readonly canDeleteDisabilityProof = hasPermission(
		this.session.permissions,
		'Documents.DisabilityReport.Delete',
	);
	private readonly sbStatusDurationRequired = new Set<DisabilityStatusInitial>([
		DisabilityStatusInitial.SEVERELY_DISABLED,
		DisabilityStatusInitial.EQUALIZED,
	]);

	public readonly shouldShowSbStatusDurationInfo = computed(() =>
		this.sbStatusDurationRequired.has(this.proof().status.initial as DisabilityStatusInitial),
	);

	// Map DisabilityType to CarouselElement for the Nav-Carousel component
	public readonly carouselElements = computed(() => {
		const neighbors = this.proofNeighbors();

		if (!neighbors) return [];

		return [neighbors.previous, neighbors.current, neighbors.next]
			.filter((dp): dp is DisabilityProof => !!dp)
			.map((dp) => ({
				id: dp.id!.toString(),
				label: dp.status.content ?? '-',
			}));
	});
	public readonly currentIndex = computed(() => {
		const proof = this.proof();
		const elements = this.carouselElements();

		if (!proof || elements.length === 0) return 0;

		const index = elements.findIndex((e) => e.id === proof.id!.toString());

		return index >= 0 ? index : 0;
	});

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['/desk/client', this.clientId(), 'disabilities', 'proofs', event.element.id]);
	}

	/**
	 * Open delete dialog, reroutes after deletion.
	 */
	public async openDialog() {
		if (this.shouldNotBeDeleted()) return;

		await lastValueFrom(
			this.dialog
				.open(DisabilityProofDeleteDialog, {
					width: '400px',
					data: {
						proof: this.proof(),
						clientId: this.clientId(),
						onDeleted: async () => {
							await this.router.navigate(['../../'], { relativeTo: this.route });
						},
					},
				})
				.afterClosed(),
		);
	}

	/**
	 * Navigates to 'edit' view.
	 */
	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	public onFileClick(file: DocumentFile) {
		const navItems = this.buildDisabilityProofNavItems(this.proof());

		this.router.navigate(['/desk/client', this.clientId(), 'documents', file.id], { state: { navItems } });
	}

	public buildDocumentEntryFromDisabilityProof(doc: DocumentFile): DocumentEntry {
		const createdAt = this.proof().since ?? new Date();

		return {
			id: this.proof().id,
			name: doc.name,
			category: DOCUMENT_CATEGORY.DISABILITY_REPORT,
			createdAt: createdAt,
			externalFileReference: this.proof().documents ?? [],
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		};
	}

	private buildDisabilityProofNavItems(proof: DisabilityProof): DocumentNavItem[] {
		const files = proof.documents ?? [];
		const entryName = proof.categoryDisabilityStatus.remark || '-';
		const createdAt = proof.since ?? new Date();

		return files.map((file) => ({
			file,
			entryId: proof.id,
			entryName,
			category: DOCUMENT_CATEGORY.DISABILITY_REPORT,
			createdAt,
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		}));
	}
}
